import React from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

function formatKickoff(iso, lang) {
  const d = new Date(iso);
  return d.toLocaleString(lang, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TeamRow({ team, score, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 shrink-0 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
          {team.logo_url ? (
            <img src={team.logo_url} alt={team.short} className="w-7 h-7 object-contain" />
          ) : (
            <span className="font-mono text-xs">{team.logo}</span>
          )}
        </div>
        <span className={`text-base truncate ${highlight ? "text-volt font-semibold" : "text-white"}`}>
          {team.name}
        </span>
      </div>
      {score !== null && score !== undefined && (
        <span className="font-display text-3xl tabular-nums score-glow">{score}</span>
      )}
    </div>
  );
}

export default function MatchCard({ match, compact = false }) {
  const { t, lang } = useI18n();
  const live = match.status === "live";
  const finished = match.status === "finished";
  const upcoming = match.status === "upcoming";

  const homeWinner = finished && match.score_home > match.score_away;
  const awayWinner = finished && match.score_away > match.score_home;

  return (
    <Link
      to={`/matches/${match.id}`}
      data-testid={`match-card-${match.id}`}
      className="block group"
    >
      <div className="card-lift bg-[#121212] border border-white/10 p-5 h-full">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs uppercase tracking-[0.2em] text-white/50">
            {match.league}
          </span>
          {live && (
            <div className="flex items-center gap-1.5">
              <span className="live-dot" />
              <span className="text-xs font-mono text-[#FF3B30] font-semibold">
                {match.minute}&apos;
              </span>
            </div>
          )}
          {finished && (
            <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-mono">
              {t("match.ft")}
            </span>
          )}
          {upcoming && (
            <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono">
              {formatKickoff(match.kickoff, lang)}
            </span>
          )}
        </div>

        <div className="space-y-3">
          <TeamRow
            team={match.home_team}
            score={live || finished ? match.score_home : null}
            highlight={live || homeWinner}
          />
          <TeamRow
            team={match.away_team}
            score={live || finished ? match.score_away : null}
            highlight={live || awayWinner}
          />
        </div>

        {!compact && (
          <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-xs text-white/40">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{match.venue}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
